import { Switch, Route, useLocation, Router as WouterRouter, Redirect } from "wouter";
import {
  QueryClient,
  QueryCache,
  MutationCache,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError, setRequestHeadersGetter } from "@workspace/api-client-react";
import { toast } from "@/hooks/use-toast";
import { getImpersonationHeaders } from "@/lib/impersonation";
import { getE2eHeaders, isE2eBypassActive } from "@/lib/e2eAuth";
import { isAuthDisabled } from "@/lib/authMode";
import { getDemoModeHeaders } from "@/lib/demoMode";

// Forward impersonation, e2e-test, and demo-mode headers on every API call
// made through the generated orval client.
setRequestHeadersGetter(() => ({
  ...getImpersonationHeaders(),
  ...getE2eHeaders(),
  ...getDemoModeHeaders(),
}));
import { I18nextProvider } from "react-i18next";
import i18n from "@/i18n";
import { ClerkProvider, SignIn, SignUp, useAuth, useClerk } from "@clerk/react";
import { shadcn } from "@clerk/themes";
import { useEffect, useRef, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";

import OnboardingPage from "@/pages/onboarding";
import TodayPage from "@/pages/today";
import ActionsPage from "@/pages/actions";
import MailPage from "@/pages/mail";
import InboxPage from "@/pages/inbox";
import ProjectsPage from "@/pages/projects";
import ProjectDetailPage from "@/pages/project-detail";
import SettingsPage from "@/pages/settings";
import ChatPage from "@/pages/chat";
import AdminPage from "@/pages/admin";
import AppShell from "@/components/app-shell";
import LandingPage from "@/pages/landing";
import WaitlistPage from "@/pages/waitlist";
import DemoPage from "@/pages/demo";
import ChangelogPage from "@/pages/changelog";
import LegalPage from "@/pages/legal";
import PricingPage from "@/pages/pricing";
import ContactPage from "@/pages/contact";
import ContactsPage from "@/pages/contacts";
import ContactDetailPage from "@/pages/contact-detail";
import LibraryPage from "@/pages/library";
import DecisionsPage from "@/pages/decisions";
import CookieBanner from "@/components/cookie-banner";
import DemoDataInjector from "@/components/demo-data-injector";
import { MailReadingProvider } from "@/lib/mail-reading-context";

// Global 402 handler: when any query/mutation hits a "payment required"
// response from the AI gating middleware, surface a toast pointing the user
// to the Forfait card in /app/settings. Avoids having to wire the same
// catch into every AI-calling component.
// Throttle the AI-budget toast — when the kill-switch is armed every
// query refresh would otherwise pop a duplicate toast.
let lastBudgetToastAt = 0;

function handleApiError(err: unknown) {
  if (err instanceof ApiError && err.status === 402) {
    const data = err.data as { error?: string; code?: string } | null;
    const isOutOfCredits = data?.code === "OUT_OF_CREDITS";
    toast({
      title: isOutOfCredits ? "Crédits IA épuisés" : "Paiement requis",
      description:
        data?.error ??
        "Vous n'avez plus de crédits IA. Rendez-vous dans Réglages → Forfait & crédits.",
      variant: "destructive",
    });
    return;
  }
  if (err instanceof ApiError && err.status === 503) {
    const data = err.data as { error?: string; code?: string } | null;
    if (data?.code === "AI_BUDGET_EXCEEDED") {
      const now = Date.now();
      if (now - lastBudgetToastAt < 30_000) return;
      lastBudgetToastAt = now;
      toast({
        title: "Service IA temporairement suspendu",
        description:
          data?.error ??
          "Le budget IA mensuel a été atteint. Réessayez plus tard.",
        variant: "destructive",
      });
    }
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleApiError }),
  mutationCache: new MutationCache({ onError: handleApiError }),
});

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL || undefined;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY in .env file");
}

const clerkAppearance = {
  theme: shadcn,
  cssLayerName: "clerk",
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo.png`,
  },
  variables: {
    colorPrimary: "hsl(217 80% 42%)",
    colorForeground: "hsl(215 30% 15%)",
    colorMutedForeground: "hsl(215 10% 45%)",
    colorDanger: "hsl(0 70% 50%)",
    colorBackground: "hsl(0 0% 100%)",
    colorInput: "hsl(215 15% 70%)",
    colorInputForeground: "hsl(215 30% 15%)",
    colorNeutral: "hsl(215 15% 70%)",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    borderRadius: "0.75rem",
  },
  elements: {
    rootBox: "w-full flex justify-center",
    cardBox: "bg-white rounded-2xl w-[440px] max-w-full overflow-hidden shadow-xl",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: "text-2xl font-semibold text-foreground tracking-tight",
    headerSubtitle: "text-muted-foreground",
    socialButtonsBlockButtonText: "font-medium",
    formFieldLabel: "font-medium text-foreground",
    footerActionLink: "font-medium text-primary hover:underline",
    footerActionText: "text-muted-foreground",
    dividerText: "text-muted-foreground text-xs uppercase tracking-wider",
    identityPreviewEditButton: "text-primary hover:underline",
    formFieldSuccessText: "text-blue-600",
    alertText: "text-sm",
    logoBox: "mb-4",
    logoImage: "h-12 w-auto object-contain",
    socialButtonsBlockButton: "border-border hover:bg-secondary transition-colors",
    formButtonPrimary: "bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium",
    formFieldInput: "bg-background border border-input rounded-md px-3 py-2 text-foreground hover:border-ring/50 focus:ring-2 focus:ring-ring/30 focus:border-ring transition-colors",
    footerAction: "pt-4 border-t border-border mt-6",
    dividerLine: "bg-border",
    alert: "border-border rounded-md",
    otpCodeFieldInputs: "gap-2 justify-center",
    otpCodeFieldInput:
      "!w-12 !h-14 !text-2xl !font-semibold !text-foreground !bg-background !border-2 !border-input !rounded-lg shadow-sm hover:!border-ring/60 focus:!ring-2 focus:!ring-primary/40 focus:!border-primary transition-colors",
    formFieldRow: "mb-4",
    main: "p-8",
  },
};

function SignInPage() {
  // In demo mode there is no real auth — bounce visitors straight in.
  if (isAuthDisabled()) {
    return <Redirect to="/app/today" />;
  }
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <SignIn
        routing="path"
        path={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
        forceRedirectUrl={`${basePath}/app/today`}
        signUpForceRedirectUrl={`${basePath}/onboarding`}
      />
    </div>
  );
}

function SignUpPage() {
  if (isAuthDisabled()) {
    return <Redirect to="/app/today" />;
  }
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <SignUp
        routing="path"
        path={`${basePath}/sign-up`}
        signInUrl={`${basePath}/sign-in`}
        forceRedirectUrl={`${basePath}/onboarding`}
        signInForceRedirectUrl={`${basePath}/app/today`}
      />
    </div>
  );
}


function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (
        prevUserIdRef.current !== undefined &&
        prevUserIdRef.current !== userId
      ) {
        queryClient.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, queryClient]);

  return null;
}

/**
 * Route "/": landing page publique pour tous.
 * Exception : E2E redirige directement vers le dashboard (test headless).
 * AUTH_DISABLED (demo) affiche la landing — elle gère elle-même l'état connecté.
 */
function HomeRedirect() {
  if (isE2eBypassActive()) {
    return <Redirect to="/app/today" />;
  }
  return <LandingPage />;
}

function RequireAuth({ children }: { children: ReactNode }) {
  // Demo mode (VITE_AUTH_DISABLED=1) and E2E bypass both render the
  // protected tree without any Clerk session check. Demo mode is for
  // a public showcase deployment; E2E mode is for Playwright runs.
  if (isAuthDisabled() || isE2eBypassActive()) {
    return <>{children}</>;
  }
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) {
    // Avoid a flash-redirect while Clerk is still bootstrapping the
    // session from cookies/localStorage.
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!isSignedIn) {
    return <Redirect to="/sign-in" />;
  }
  return <>{children}</>;
}

/**
 * Captures ?ref=CODE from the URL at app load and stores it in localStorage
 * so it survives the Clerk sign-up flow (which redirects several times).
 */
function useReferralCapture() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref && /^[A-Z0-9_-]{2,50}$/i.test(ref)) {
        localStorage.setItem("onenext.referral", ref.toUpperCase());
      }
    } catch {
      // localStorage may be blocked in some contexts — ignore silently.
    }
  }, []);
}

/**
 * After the user signs in, claims any pending referral code stored in
 * localStorage and links it to their account. Runs once per session.
 */
function ReferralClaimer() {
  const { isSignedIn, isLoaded } = useAuth();
  const claimedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || claimedRef.current) return;
    const code = (() => {
      try {
        return localStorage.getItem("onenext.referral");
      } catch {
        return null;
      }
    })();
    if (!code) return;

    claimedRef.current = true;
    fetch("/api/referral/claim", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    })
      .catch(() => {
        // Non-critical — ignore failures silently.
      })
      .finally(() => {
        try {
          localStorage.removeItem("onenext.referral");
        } catch {
          /* ignore */
        }
      });
  }, [isLoaded, isSignedIn]);

  return null;
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location]);
  return null;
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Switch>
      <Route path="/" component={HomeRedirect} />
      <Route path="/landing">
        <Redirect to="/" />
      </Route>
      <Route path="/changelog" component={ChangelogPage} />
      <Route path="/sign-in/*?" component={SignInPage} />
      <Route path="/sign-up/*?" component={SignUpPage} />
      <Route path="/legal" component={LegalPage} />
      <Route path="/legal/:slug" component={LegalPage} />
      <Route path="/pricing" component={PricingPage} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/demo" component={DemoPage} />
      <Route path="/waitlist" component={WaitlistPage} />
      <Route path="/onboarding">
        <RequireAuth>
          <OnboardingPage />
        </RequireAuth>
      </Route>
      <Route path="/admin/posts">
        <RequireAuth>
          <AppShell>
            <AdminPage defaultTab="posts" />
          </AppShell>
        </RequireAuth>
      </Route>
      <Route path="/admin">
        <RequireAuth>
          <AppShell>
            <AdminPage />
          </AppShell>
        </RequireAuth>
      </Route>
      <Route path="/app/*?">
        <RequireAuth>
          <AppShell>
            <Switch>
              <Route path="/app/today" component={TodayPage} />
              <Route path="/app/actions" component={ActionsPage} />
              <Route path="/app/mail" component={MailPage} />
              <Route path="/app/inbox" component={InboxPage} />
              <Route path="/app/projects" component={ProjectsPage} />
              <Route path="/app/projects/:id" component={ProjectDetailPage} />
              <Route path="/app/crm" component={ContactsPage} />
              <Route path="/app/crm/:id" component={ContactDetailPage} />
              <Route path="/app/chat" component={ChatPage} />
              <Route path="/app/chat/:id" component={ChatPage} />
              <Route path="/app/library" component={LibraryPage} />
              <Route path="/app/decisions" component={DecisionsPage} />
              <Route path="/app/settings" component={SettingsPage} />
              <Route component={NotFound} />
            </Switch>
          </AppShell>
        </RequireAuth>
      </Route>
      <Route component={NotFound} />
    </Switch>
    </>
  );
}

function ClerkProviderWithRoutes() {
  useReferralCapture();
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={{
        socialButtonsBlockButton: "Continuer avec {{provider|titleize}}",
        dividerText: "ou",
        formFieldLabel__emailAddress: "Adresse e-mail",
        formFieldLabel__password: "Mot de passe",
        formFieldLabel__confirmPassword: "Confirmez le mot de passe",
        formFieldLabel__firstName: "Prénom",
        formFieldLabel__lastName: "Nom",
        formButtonPrimary: "Continuer",
        backButton: "Retour",
        signIn: {
          start: {
            title: "Bienvenue",
            subtitle: "Connectez-vous pour accéder à votre compte",
            actionText: "Pas encore de compte ?",
            actionLink: "Créer un compte",
          },
          password: {
            title: "Entrez votre mot de passe",
            subtitle: "pour continuer vers OneNext",
            actionLink: "Mot de passe oublié ?",
          },
          forgotPasswordAlternativeMethods: {
            blockButton__resetPassword: "Réinitialiser le mot de passe",
            label__alternativeMethods: "Ou choisissez une autre méthode",
            title: "Mot de passe oublié ?",
          },
          emailCode: {
            title: "Vérifiez votre e-mail",
            subtitle: "pour continuer vers OneNext",
            formTitle: "Code de vérification",
            resendButton: "Renvoyer le code",
          },
        },
        signUp: {
          start: {
            title: "Créez votre compte",
            subtitle: "Commencez dès aujourd'hui",
            actionText: "Vous avez déjà un compte ?",
            actionLink: "Se connecter",
          },
          emailCode: {
            title: "Vérifiez votre e-mail",
            subtitle: "pour activer votre compte",
            formTitle: "Code de vérification",
            resendButton: "Renvoyer le code",
          },
          continue: {
            title: "Encore quelques détails",
            subtitle: "pour finaliser votre compte",
            actionText: "Vous avez déjà un compte ?",
            actionLink: "Se connecter",
          },
        },
        userButton: {
          action__signOut: "Se déconnecter",
          action__manageAccount: "Gérer le compte",
        },
        formFieldError__notMatchingPasswords: "Les mots de passe ne correspondent pas",
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkQueryClientCacheInvalidator />
        <DemoDataInjector />
        <ReferralClaimer />
        <AppRoutes />
        <CookieBanner />
      </QueryClientProvider>
    </ClerkProvider>
  );
}

export default function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <TooltipProvider>
        <MailReadingProvider>
          <WouterRouter base={basePath}>
            <ClerkProviderWithRoutes />
          </WouterRouter>
          <Toaster />
        </MailReadingProvider>
      </TooltipProvider>
    </I18nextProvider>
  );
}
